import { ApiResponse } from 'apisauce';
import { useCallback, useMemo } from 'react';
import config from '../../config';
import { useApplicationContext } from '../context/ApplicationContext/ApplicationContext';
import { InstagramConfig, instagramRepository } from './instagram.repository';

const INSTAGRAM_SETTINGS_PAGE = 'settings/instagram';

const instagramParams = {
  response_type: 'code',
  scope: 'user_profile,user_media',
  client_id: config.instagramClientId,
};

export type OwnerType = 'brand' | 'store';

type InstagramConfigHook = {
  initInstagramConfig: () => void;
  validateCode: (code: string) => Promise<ApiResponse<any>>;
  removeConfig: (ownerType: OwnerType) => Promise<ApiResponse<any>>;
  loadConfig: (ownerType: OwnerType, id: string) => Promise<ApiResponse<InstagramConfig>>;
};

const repository = instagramRepository();

const useInstagramConfig = (): InstagramConfigHook => {
  const { isAdminBrand } = useApplicationContext();

  const instagramAuthUrlWithRedirect = useMemo(() => {
    const redirectUri = `${window.location.origin}/${INSTAGRAM_SETTINGS_PAGE}/`;
    const instagramAuthUrl = new URL(`${config.instagramOAuthUrl}/authorize`);
    instagramAuthUrl.search = new URLSearchParams({
      ...instagramParams,
      redirect_uri: redirectUri,
    }).toString();
    return instagramAuthUrl.toString();
  }, []);

  const initInstagramConfig = () => {
    window.location.href = instagramAuthUrlWithRedirect;
  };

  const validateCode = async (code: string) => {
    const validateApiMethod = isAdminBrand
      ? repository.validateCodeBrand
      : repository.validateCodeStore;
    return validateApiMethod(code);
  };

  const removeConfig = useCallback(async (type: OwnerType) => {
    const configRemover =
      type === 'brand' ? repository.removeConfigBrand : repository.removeConfigStore;
    return configRemover();
  }, []);

  const loadConfig = useCallback(async (type: OwnerType, ownerId: string) => {
    const configLoader =
      type === 'brand' ? repository.getInstagramBrand : repository.getInstagramStore;
    return configLoader(ownerId);
  }, []);

  return {
    loadConfig,
    initInstagramConfig,
    validateCode,
    removeConfig,
  };
};

export default useInstagramConfig;
